import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Typography, useTheme, useMediaQuery, alpha } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useEditUserResponseDialogStyles } from './useEditUserResponseDialogStyles';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import HTimeline from '@ui/components/Timeline';
import fieldRenderers from '@ui/pages/EventRegistration/RegistrationForm/fieldRenderers';
import { REGISTRATION_FIELD_FILL_BG } from '@ui/pages/EventRegistration/RegistrationForm/registrationFormFieldStyles';
import AdditionalAttendeesGroup from '@ui/components/AdditionalAttendeesGroup';
import { useAppSelector, useAppDispatch } from '@application/store/useStore';
import { APIStatus } from '@application/enums/api-status';
import { updateResponsesAsync, getAllActiveResponsesByEventIdGroupedByRegistrationAsync } from '@application/features/vdkEventResponses/vdkEventResponsesThunk';
import { FieldType } from '@application/enums/field-types';
import { checkPickupLocationAvailabilityAsync, getPickupLocationsNotPaginatedByEventIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
const EditUserResponseDialog = ({ open, onClose, userId, userName, eventId, paginationModel }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(1200));
    const styles = useEditUserResponseDialogStyles();
    const dispatch = useAppDispatch();
    // Get steps, fields, and responses from Redux store
    const { stepsFieldsResponses, status } = useAppSelector((state) => state.vdkEventResponse);
    // Get event data to access isExternalAllowed
    const { VDKEventsForEventDetails, VDKEvents } = useAppSelector((state) => state.VDKEvent);
    const currentEvent = VDKEventsForEventDetails || (VDKEvents ?? []).find((e) => e.id === eventId);
    const { pickupLocationsList } = useAppSelector((state) => state.pickupLocation);
    // Store referredBy fields grouped by parent company dropdown ID
    const [referredByFieldsByParent, setReferredByFieldsByParent] = useState({});
    // Additional Attendees' `people` array lives behind react-hook-form's useFieldArray inside
    // AdditionalAttendeesGroup - a plain setValue() on that path updates the form's values but not
    // useFieldArray's own `fields` bookkeeping, so the group would keep rendering whatever it had at
    // mount (usually nothing) regardless of what we hydrate here. Passed down as a prop instead, so
    // the group can hydrate itself via useFieldArray's own `replace()`.
    const [additionalAttendeesInitialPeopleByParent, setAdditionalAttendeesInitialPeopleByParent] = useState({});
    // Add loading state for save operation
    const [isSaving, setIsSaving] = useState(false);
    const [hasBlockingPickupLocation, setHasBlockingPickupLocation] = useState(false);
    const [pickupLocationLoading, setPickupLocationLoading] = useState({});
    const [pickupLocationError, setPickupLocationError] = useState({});
    const { control, handleSubmit, watch, setValue, getValues, setError, clearErrors, reset, formState: { errors } } = useForm({
        mode: 'all',
        reValidateMode: 'onChange'
    });
    const watchedValues = watch();
    const pickupLocationOptions = useMemo(() => {
        const pickupLocations = pickupLocationsList || [];
        return pickupLocations.map((loc, index) => ({
            id: loc.id,
            label: loc.name,
            value: String(index + 1),
            description: loc.description ?? loc.address ?? ''
        }));
    }, [pickupLocationsList]);
    const pickupLocationFieldIds = useMemo(() => {
        return (stepsFieldsResponses || []).flatMap((step) => (step.formFields || [])
            .filter((field) => String(field.fieldType) === String(FieldType.PICKUPLOCATION))
            .map((field) => field.id));
    }, [stepsFieldsResponses]);
    // Load event pickup locations (for field type 20) when dialog opens
    useEffect(() => {
        if (open && eventId) {
            dispatch(getPickupLocationsNotPaginatedByEventIdAsync({
                eventId,
                includeClosed: true
            }));
        }
    }, [open, eventId, dispatch]);
    // Initialize form values with response data
    useEffect(() => {
        if (open && stepsFieldsResponses) {
            // Reset form first to clear any previous data
            reset();
            const allFieldsForHydration = stepsFieldsResponses.flatMap((s) => s.formFields || []);
            const additionalAttendeesParents = allFieldsForHydration.filter((f) => f.fieldType === FieldType.ADDITIONAL_ATTENDEES);
            stepsFieldsResponses.forEach((step) => {
                step.formFields?.forEach((field) => {
                    // Additional-Attendees sub-questions can have more than one response row (one per
                    // repeated person) - hydrated separately below, grouped by person, instead of through
                    // this generic single-response-per-field path.
                    const subFieldParentId = field.relatedFormFieldId || field.RelatedFormFieldId;
                    if (subFieldParentId &&
                        additionalAttendeesParents.some((p) => p.id === subFieldParentId)) {
                        return;
                    }
                    const response = field.fieldResponses?.[0];
                    if (response) {
                        // Handle different types of responses
                        if (response.selectedOptions && response.selectedOptions.length > 0) {
                            // For multi-select fields, use the option values
                            const optionValues = response.selectedOptions.map((option) => String(option.value));
                            setValue(field.id, optionValues);
                        }
                        else if (response.responseValue &&
                            response.responseValue !== 'null' &&
                            response.responseValue !== '') {
                            // For single value fields or when selectedOptions is empty
                            let parsedValue;
                            try {
                                // Try to parse JSON string first (for cases like "[\"1\"]")
                                parsedValue = JSON.parse(response.responseValue);
                            }
                            catch {
                                // If not JSON, use the value as is
                                parsedValue = response.responseValue;
                            }
                            if (field.fieldType === FieldType.ADDITIONAL_ATTENDEES) {
                                // Additional Attendees: responseValue is a JSON array of person objects, not a
                                // list of scalars - hydrate it as-is under `${fieldId}.people` for
                                // AdditionalAttendeesGroup's useFieldArray to pick up.
                                setValue(`${field.id}.people`, Array.isArray(parsedValue) ? parsedValue : []);
                            }
                            else if (Array.isArray(parsedValue)) {
                                // Handle array response values like ["1","2"]
                                setValue(field.id, parsedValue.map((val) => String(val)));
                            }
                            else {
                                // Handle single response values based on field type
                                if (field.fieldType === '4') {
                                    // Date field - convert to Date object
                                    const dateValue = new Date(parsedValue);
                                    if (!isNaN(dateValue.getTime())) {
                                        setValue(field.id, dateValue);
                                    }
                                    else {
                                        setValue(field.id, null);
                                    }
                                }
                                else if (field.fieldType === '5') {
                                    // Time field - always convert to Date object for consistency
                                    // This ensures ETimePicker receives the expected type
                                    let timeValue = null;
                                    const timeStr = String(parsedValue).trim();
                                    if (timeStr.includes('/') && !timeStr.includes(':')) {
                                        // This is a date string like "01/09/2025" - convert to Date
                                        timeValue = new Date(timeStr);
                                    }
                                    else if (timeStr.includes(':')) {
                                        // This is a time string like "04:20 PM" - convert to Date
                                        // Create a Date object with today's date and the time
                                        const today = new Date();
                                        // Try different time formats
                                        let timeParts = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
                                        if (timeParts) {
                                            // 12-hour format with AM/PM
                                            let hours = parseInt(timeParts[1]);
                                            const minutes = parseInt(timeParts[2]);
                                            const isPM = timeParts[3].toUpperCase() === 'PM';
                                            if (isPM && hours !== 12)
                                                hours += 12;
                                            if (!isPM && hours === 12)
                                                hours = 0;
                                            today.setHours(hours, minutes, 0, 0);
                                            timeValue = today;
                                        }
                                        else {
                                            // Try 24-hour format
                                            timeParts = timeStr.match(/(\d{1,2}):(\d{2})/);
                                            if (timeParts) {
                                                const hours = parseInt(timeParts[1]);
                                                const minutes = parseInt(timeParts[2]);
                                                today.setHours(hours, minutes, 0, 0);
                                                timeValue = today;
                                            }
                                            else {
                                                // Fallback: try to parse as generic time
                                                timeValue = new Date(`2000-01-01T${timeStr}`);
                                            }
                                        }
                                    }
                                    else {
                                        // Try to parse as generic date/time
                                        timeValue = new Date(timeStr);
                                    }
                                    if (timeValue && !isNaN(timeValue.getTime())) {
                                        setValue(field.id, timeValue);
                                    }
                                    else {
                                        setValue(field.id, null);
                                    }
                                }
                                else if (field.fieldType === '12') {
                                    // Toggle field - convert to boolean
                                    const value = String(parsedValue).toLowerCase().trim();
                                    setValue(field.id, value === 'true' || value === '1' || value === 'yes');
                                }
                                else if (field.fieldType === '18') {
                                    // Company dropdown field - handle both selected options and newly added companies
                                    if (response.companyId &&
                                        response.companyId !== null &&
                                        response.companyId !== '') {
                                        // Use the CompanyId to find the matching option for preselection
                                        const matchingOption = field.fieldOptions?.find((opt) => opt.id === response.companyId);
                                        if (matchingOption) {
                                            setValue(field.id, String(matchingOption.value));
                                        }
                                        else {
                                            // If no matching option found, try to use responseValue as fallback
                                            setValue(field.id, String(parsedValue));
                                        }
                                    }
                                    else if (response.optionId) {
                                        // This is a selected option - use the responseValue as is
                                        setValue(field.id, String(parsedValue));
                                    }
                                    else if (response.responseValue &&
                                        response.responseValue !== 'null' &&
                                        response.responseValue !== '') {
                                        // This is a newly added company - use the responseValue as is
                                        setValue(field.id, String(parsedValue));
                                    }
                                    else {
                                        // No response or null response - keep as null
                                        setValue(field.id, null);
                                    }
                                }
                                else if (field.fieldType === FieldType.PICKUPLOCATION) {
                                    // Pickup location dropdown field - preselect based on optionId when available
                                    if (response.optionId && pickupLocationOptions.length) {
                                        const matchingOption = pickupLocationOptions.find((opt) => String(opt.id) === String(response.optionId));
                                        const valueToSet = matchingOption
                                            ? String(matchingOption.value)
                                            : String(parsedValue);
                                        setValue(field.id, valueToSet);
                                    }
                                    else {
                                        setValue(field.id, String(parsedValue));
                                    }
                                }
                                else if (field.fieldType === FieldType.UNDERTAKING) {
                                    // Undertaking field - set acknowledgment status and reason
                                    const responseValue = String(parsedValue).toLowerCase().trim();
                                    const acknowledgmentStatus = responseValue === 'true' || responseValue === '1' || responseValue === 'yes';
                                    setValue(field.id, acknowledgmentStatus);
                                    // For undertaking fields, always set a reason field to ensure proper display
                                    // Check for acknowledgment reason in different possible properties
                                    let reasonText = '';
                                    if (response.acknowledgmentReason) {
                                        reasonText = response.acknowledgmentReason;
                                    }
                                    else if (response.reason) {
                                        reasonText = response.reason;
                                    }
                                    else {
                                        // If no specific reason found, use a default based on status
                                        reasonText = acknowledgmentStatus ? 'Acknowledged' : 'Declined';
                                    }
                                    setValue(`${field.id}_reason`, reasonText);
                                }
                                else {
                                    // Other field types - convert to string
                                    setValue(field.id, String(parsedValue));
                                }
                            }
                        }
                        else {
                            // No response or null response - explicitly set to null for company dropdowns
                            if (field.fieldType === '18') {
                                setValue(field.id, null);
                            }
                        }
                    }
                    else {
                        // No response at all - explicitly set to null for company dropdowns
                        if (field.fieldType === '18') {
                            setValue(field.id, null);
                        }
                    }
                });
            });
            // Hydrate Additional Attendees: reconstruct each person's answers from their sub-questions'
            // (possibly several) FieldResponse rows, grouped by which repeated instance they belong to.
            const initialPeopleByParent = {};
            additionalAttendeesParents.forEach((parent) => {
                const subFields = allFieldsForHydration.filter((f) => (f.relatedFormFieldId || f.RelatedFormFieldId) === parent.id);
                const instanceOrder = [];
                const peopleByInstance = {};
                subFields.forEach((subField) => {
                    (subField.fieldResponses || []).forEach((response) => {
                        const instanceKey = response.attendeeGroupInstanceId || response.AttendeeGroupInstanceId || 'default';
                        if (!peopleByInstance[instanceKey]) {
                            peopleByInstance[instanceKey] = {};
                            instanceOrder.push(instanceKey);
                        }
                        peopleByInstance[instanceKey][subField.id] = response.responseValue;
                    });
                });
                const people = instanceOrder.map((key) => peopleByInstance[key]);
                if (people.length > 0) {
                    initialPeopleByParent[parent.id] = people;
                }
            });
            setAdditionalAttendeesInitialPeopleByParent(initialPeopleByParent);
        }
    }, [open, reset, setValue, stepsFieldsResponses, currentEvent, pickupLocationOptions]);
    // Clear form data when dialog closes or when userId changes
    useEffect(() => {
        if (!open) {
            reset();
            setAdditionalAttendeesInitialPeopleByParent({});
            // Reset form when dialog closes
            // Note: We can't clear the Redux store here, but the form will be reset
        }
    }, [open, reset]);
    // Reset form when userId changes (switching between different users)
    useEffect(() => {
        if (open && userId) {
            reset();
        }
    }, [userId, reset, open]);
    // Group referredBy fields by parent company dropdown when stepsFieldsResponses is loaded
    useEffect(() => {
        if (stepsFieldsResponses) {
            const referredByByParent = {};
            stepsFieldsResponses.forEach((step) => {
                step.formFields?.forEach((field) => {
                    const parentId = field.relatedFormFieldId || field.RelatedFormFieldId;
                    if (parentId) {
                        if (!referredByByParent[parentId]) {
                            referredByByParent[parentId] = [];
                        }
                        referredByByParent[parentId].push({
                            ...field,
                            fieldType: field.fieldType?.toString() || field.fieldTypeId?.toString() || '1',
                            isRequired: field.isRequired || field.required || false,
                            isEnabled: field.isEnabled === true || field.isEnabled === undefined,
                            fieldOptions: field.fieldOptions || field.options || []
                        });
                    }
                });
            });
            setReferredByFieldsByParent(referredByByParent);
        }
    }, [stepsFieldsResponses]);
    const getAllFormFields = () => (stepsFieldsResponses || []).flatMap((step) => step.formFields || []);
    const getOptionToParentFieldMap = () => {
        const map = {};
        getAllFormFields().forEach((field) => {
            (field.fieldOptions || field.options || []).forEach((option) => {
                if (option?.id) {
                    map[String(option.id)] = field.id;
                }
            });
        });
        return map;
    };
    const getSelectedOptionIds = (field) => {
        const currentValue = watch(field.id);
        const toComparableValues = (value) => {
            if (typeof value === 'boolean')
                return value ? ['1', 'true'] : ['0', 'false'];
            return [String(value)];
        };
        const selectedValues = Array.isArray(currentValue)
            ? currentValue.flatMap((v) => toComparableValues(v))
            : currentValue !== undefined && currentValue !== null && currentValue !== ''
                ? toComparableValues(currentValue)
                : [];
        const selectedValuesSet = new Set(selectedValues.map((v) => String(v).toLowerCase()));
        if (selectedValues.length === 0)
            return [];
        const fieldOptions = field.fieldOptions || field.options || [];
        return fieldOptions
            .filter((option) => selectedValuesSet.has(String(option.value).toLowerCase()))
            .map((option) => String(option.id));
    };
    const isBranchingFieldVisible = (field) => {
        const parentOptionIds = Array.isArray(field.parentOptionIds)
            ? field.parentOptionIds
            : (field.parentOptionId ?? field.ParentOptionId)
                ? [String(field.parentOptionId ?? field.ParentOptionId)]
                : [];
        if (parentOptionIds.length === 0)
            return true;
        const optionToParentField = getOptionToParentFieldMap();
        const parentFieldId = optionToParentField[String(parentOptionIds[0])];
        if (!parentFieldId)
            return false;
        const parentField = getAllFormFields().find((f) => f.id === parentFieldId);
        if (!parentField)
            return false;
        const selectedParentOptionIds = getSelectedOptionIds(parentField);
        return parentOptionIds.some((id) => selectedParentOptionIds.includes(String(id)));
    };
    const getSelectedOptionIdsByValue = (field, value) => {
        const selectedValues = Array.isArray(value)
            ? value.map((v) => String(v))
            : value !== undefined && value !== null && value !== ''
                ? [String(value)]
                : [];
        if (selectedValues.length === 0)
            return [];
        const fieldOptions = field.fieldOptions || field.options || [];
        return fieldOptions
            .filter((option) => selectedValues.includes(String(option.value)))
            .map((option) => String(option.id))
            .sort();
    };
    const getInitialSelectedOptionIds = (field) => {
        const response = field.fieldResponses?.[0];
        if (!response)
            return [];
        // Prefer selectedOptions from response when present
        if (Array.isArray(response.selectedOptions) && response.selectedOptions.length > 0) {
            const idsFromResponse = response.selectedOptions
                .map((opt) => opt.optionId || opt.id)
                .filter(Boolean)
                .map((id) => String(id))
                .sort();
            if (idsFromResponse.length > 0) {
                return idsFromResponse;
            }
            // Fallback: resolve by selected option values
            const selectedValues = response.selectedOptions.map((opt) => String(opt.value));
            const fieldOptions = field.fieldOptions || field.options || [];
            return fieldOptions
                .filter((option) => selectedValues.includes(String(option.value)))
                .map((option) => String(option.id))
                .sort();
        }
        // Fallback: resolve from responseValue against field option values
        if (response.responseValue &&
            response.responseValue !== 'null' &&
            response.responseValue !== '') {
            let parsed = response.responseValue;
            try {
                parsed = JSON.parse(response.responseValue);
            }
            catch { }
            return getSelectedOptionIdsByValue(field, parsed);
        }
        return [];
    };
    const getUndertakingParentOptionIds = () => {
        const allFields = getAllFormFields();
        return new Set(allFields
            .filter((f) => String(f.fieldType) === String(FieldType.UNDERTAKING))
            .map((f) => f.parentOptionId ?? f.ParentOptionId)
            .filter(Boolean)
            .map((id) => String(id)));
    };
    const isUndertakingLinkedParentField = (field) => {
        const undertakingParentOptionIds = getUndertakingParentOptionIds();
        if (undertakingParentOptionIds.size === 0)
            return false;
        const fieldOptions = field.fieldOptions || field.options || [];
        return fieldOptions.some((option) => undertakingParentOptionIds.has(String(option.id)));
    };
    const getFieldProps = (field) => {
        // Get the current response value for this field
        const currentResponse = field.fieldResponses?.[0];
        let defaultValue = undefined;
        if (currentResponse) {
            if (currentResponse.selectedOptions && currentResponse.selectedOptions.length > 0) {
                // For multi-select fields, use the option values
                defaultValue = currentResponse.selectedOptions.map((option) => String(option.value));
            }
            else if (currentResponse.responseValue) {
                // For single value fields or when selectedOptions is empty
                let parsedValue;
                try {
                    // Try to parse JSON string first (for cases like "[\"1\"]")
                    parsedValue = JSON.parse(currentResponse.responseValue);
                }
                catch {
                    // If not JSON, use the value as is
                    parsedValue = currentResponse.responseValue;
                }
                if (Array.isArray(parsedValue)) {
                    // Handle array response values like ["1","2"]
                    defaultValue = parsedValue.map((val) => String(val));
                }
                else {
                    // For date fields, don't set defaultValue here
                    // Let the useEffect handle setting the form values
                    if (field.fieldType === '4') {
                        defaultValue = undefined;
                    }
                    else if (field.fieldType === '5') {
                        // Time fields - always convert to Date object for consistency
                        let timeValue = null;
                        if (String(parsedValue).includes('/') && !String(parsedValue).includes(':')) {
                            // This is a date string like "01/09/2025" - convert to Date
                            timeValue = new Date(parsedValue);
                        }
                        else if (String(parsedValue).includes(':')) {
                            // This is a time string like "04:20 PM" - convert to Date
                            // Create a Date object with today's date and the time
                            const today = new Date();
                            const timeParts = String(parsedValue).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
                            if (timeParts) {
                                let hours = parseInt(timeParts[1]);
                                const minutes = parseInt(timeParts[2]);
                                const isPM = timeParts[3].toUpperCase() === 'PM';
                                if (isPM && hours !== 12)
                                    hours += 12;
                                if (!isPM && hours === 12)
                                    hours = 0;
                                today.setHours(hours, minutes, 0, 0);
                                timeValue = today;
                            }
                            else {
                                // Fallback: try to parse as generic time
                                timeValue = new Date(`2000-01-01T${parsedValue}`);
                            }
                        }
                        else {
                            // Try to parse as generic date/time
                            timeValue = new Date(parsedValue);
                        }
                        defaultValue = timeValue && !isNaN(timeValue.getTime()) ? timeValue : null;
                    }
                    else if (field.fieldType === '12') {
                        // Toggle field - convert to boolean
                        const value = String(parsedValue).toLowerCase().trim();
                        defaultValue = value === 'true' || value === '1' || value === 'yes';
                    }
                    else if (field.fieldType === '18') {
                        // Company dropdown field - handle both selected options and newly added companies
                        if (currentResponse.companyId &&
                            currentResponse.companyId !== null &&
                            currentResponse.companyId !== '') {
                            // Use the CompanyId to find the matching option for preselection
                            const matchingOption = field.fieldOptions?.find((opt) => opt.id === currentResponse.companyId);
                            if (matchingOption) {
                                defaultValue = String(matchingOption.value);
                            }
                            else {
                                // If no matching option found, try to use responseValue as fallback
                                defaultValue = String(parsedValue);
                            }
                        }
                        else if (currentResponse.optionId) {
                            // This is a selected option - use the responseValue as is
                            defaultValue = String(parsedValue);
                        }
                        else if (currentResponse.responseValue &&
                            currentResponse.responseValue !== 'null' &&
                            currentResponse.responseValue !== '') {
                            // This is a newly added company - use the responseValue as is
                            defaultValue = String(parsedValue);
                        }
                        else {
                            // No response or null response - keep as null
                            defaultValue = null;
                        }
                    }
                    else {
                        // Other field types - convert to string
                        defaultValue = String(parsedValue);
                    }
                }
            }
        }
        const common = {
            key: field.id,
            name: field.id,
            control,
            label: field.label,
            bgColor: theme.palette.common.grey8,
            border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            selectedTone: 'darker',
            defaultValue: defaultValue,
            disabled: isUndertakingLinkedParentField(field),
            rules: {
                required: field.isRequired ? field.requiredErrorMsg || 'This field is required' : false,
                validate: (value) => {
                    if (!isBranchingFieldVisible(field)) {
                        return true;
                    }
                    // Only validate if the field is required
                    if (!field.isRequired)
                        return true;
                    // Check for empty values
                    if (!value || value === '') {
                        return field.requiredErrorMsg || 'This field is required';
                    }
                    // Check for empty arrays (for multi-select fields)
                    if (Array.isArray(value) && value.length === 0) {
                        return field.requiredErrorMsg || 'This field is required';
                    }
                    // Check for whitespace-only strings
                    if (typeof value === 'string' && value.trim() === '') {
                        return field.requiredErrorMsg || 'This field is required';
                    }
                    return true;
                }
            }
        };
        if (field.fieldOptions) {
            common.options = field.fieldOptions.map((opt) => ({
                label: opt.label,
                value: String(opt.value),
                id: opt.id
            }));
        }
        if (field.fieldType === '2') {
            common.rules = {
                ...common.rules,
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' }
            };
        }
        if (field.fieldType === '6') {
            common.multiline = true;
            common.rows = 4;
        }
        if (field.fieldType === '8' || String(field.fieldType) === String(FieldType.CHOICE)) {
            common.bgColor = REGISTRATION_FIELD_FILL_BG;
            common.notSelectedBgColor = 'transparent';
        }
        if (field.fieldType === '5') {
            common.selectedTone = 'darker';
        }
        if (field.fieldType === '4') {
            common.selectedTone = 'darker';
        }
        // Undertaking field (field type 19) - readonly in edit dialog
        if (field.fieldType === '19') {
            const currentResponse = field.fieldResponses?.[0];
            const undertakingFile = currentResponse?.undertakingFiles?.[0];
            common.title = field.documentTitle || field.label || 'Document';
            common.fileName = undertakingFile?.name || 'document.pdf';
            common.content = field.documentContent || '';
            common.pdfUrl = undertakingFile?.url || field.pdfUrl || field.documentUrl || '';
            common.readonly = true; // Make it readonly in edit dialog
            // Set the reason field name to match the field ID + "_reason"
            common.reasonName = `${field.id}_reason`;
            // If there's a response, set the acknowledgment status and reason for display
            if (currentResponse) {
                const responseValue = currentResponse.responseValue;
                if (responseValue === 'true' || responseValue === true) {
                    common.defaultValue = true;
                }
                else if (responseValue === 'false' || responseValue === false) {
                    common.defaultValue = false;
                }
                // Get the acknowledgment reason for display
                const acknowledgmentReason = currentResponse?.acknowledgmentReason || currentResponse?.reason || '';
                if (acknowledgmentReason) {
                    common.acknowledgmentReason = acknowledgmentReason;
                }
            }
        }
        // EAutocompleteWithCreate (field type 18) - Company dropdown
        if (field.fieldType === '18') {
            const isExternalAllowed = currentEvent?.isExternalAllowed || false;
            common.allowCreate = isExternalAllowed;
            common.createOptionText = t('common.add');
            common.onCreateOption = (label) => ({
                label: label,
                value: isExternalAllowed ? label : label.toLowerCase(),
                isNew: true
            });
            // For company dropdown, populate options ONLY from event companies
            let freshOptions = [];
            // Only add event companies as options
            if (currentEvent?.eventCompanies && currentEvent.eventCompanies.length > 0) {
                const eventCompanyOptions = currentEvent.eventCompanies.map((company, index) => ({
                    id: company.value,
                    label: company.label,
                    value: String(index + 1),
                    isNew: false
                }));
                freshOptions = [...eventCompanyOptions];
            }
            // For update case: Handle both selected options and newly added companies
            if (currentResponse && currentResponse.responseValue) {
                const responseValue = String(currentResponse.responseValue);
                const optionId = currentResponse.optionId;
                const companyId = currentResponse.companyId;
                if (companyId) {
                    // This is a company selected from event companies - find the matching option by companyId
                    const matchingOption = freshOptions.find((opt) => opt.id === companyId);
                    if (matchingOption) {
                        // The option exists, no need to add it
                        // The form will automatically select it based on the matching option's value
                    }
                }
                else if (optionId) {
                    // This is a selected option from the dropdown - find the matching option
                    const matchingOption = freshOptions.find((opt) => opt.id === optionId);
                    if (matchingOption) {
                        // The option exists, no need to add it
                        // The form will automatically select it based on the responseValue
                    }
                }
                else {
                    // This is a newly added company (like "Jakarta") - add it to options
                    const currentLabel = responseValue;
                    const currentValue = responseValue;
                    // Check if the current value is already in the fresh options
                    const isValueInOptions = freshOptions.some((opt) => opt.value === currentValue);
                    if (!isValueInOptions) {
                        // Add the current response value as an option so it shows in the dropdown
                        const currentOption = {
                            label: currentLabel,
                            value: currentValue,
                            id: null,
                            isNew: true
                        };
                        // Add to the beginning of options so it appears first
                        freshOptions = [currentOption, ...freshOptions];
                    }
                }
            }
            // Assign the fresh options array
            common.options = freshOptions;
        }
        // Pickup location dropdown (field type 20)
        if (String(field.fieldType) === String(FieldType.PICKUPLOCATION)) {
            common.multiple = false;
            common.options = pickupLocationOptions;
        }
        return common;
    };
    const renderFieldWithResponse = (field) => {
        if (String(field.fieldType) === String(FieldType.ADDITIONAL_ATTENDEES)) {
            return (_jsx(AdditionalAttendeesGroup, { fieldId: field.id, label: field.label, maxAdditionalAttendees: field.maxAdditionalAttendees ?? field.MaxAdditionalAttendees ?? 2, maxAdditionalAttendeesReachedMessage: field.maxAdditionalAttendeesReachedMessage ?? field.MaxAdditionalAttendeesReachedMessage, initialPeople: additionalAttendeesInitialPeopleByParent[field.id], subFieldDefinitions: (referredByFieldsByParent[field.id] || [])
                    .slice()
                    .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                    .map((subField) => ({
                    id: subField.id,
                    label: subField.label,
                    fieldType: subField.fieldType,
                    isRequired: Boolean(subField.isRequired),
                    fieldOptions: subField.fieldOptions || []
                })), control: control }, field.id));
        }
        const FieldComponent = fieldRenderers[field.fieldType];
        if (!FieldComponent) {
            return (_jsxs(Box, { sx: { width: '100%', mb: 2 }, children: [_jsx(Typography, { variant: 'body1', sx: { fontWeight: 600 }, children: field.label }), _jsxs(Typography, { variant: 'body2', color: 'text.secondary', children: ["Field type \"", field.fieldType, "\" not supported"] })] }, field.id));
        }
        const { key, ...restProps } = getFieldProps(field);
        const fieldError = errors[field.id]?.message;
        return (_jsx(FieldComponent, { error: fieldError, ...restProps }, `${key}-${userId}-${field.id}`));
    };
    const checkPickupLocationById = async (name, selectedValue) => {
        if (!selectedValue || selectedValue.trim() === '') {
            setHasBlockingPickupLocation(false);
            setPickupLocationError((prev) => ({ ...prev, [name]: null }));
            clearErrors(name);
            return false;
        }
        const matchedOption = pickupLocationOptions.find((opt) => String(opt.value) === String(selectedValue));
        if (!matchedOption)
            return false;
        const locationId = String(matchedOption.id);
        setHasBlockingPickupLocation(false);
        setPickupLocationLoading((prev) => ({ ...prev, [name]: true }));
        try {
            const response = await dispatch(checkPickupLocationAvailabilityAsync(locationId)).unwrap();
            const isClosed = response?.data?.isClosed;
            setHasBlockingPickupLocation(isClosed || false);
            setPickupLocationError((prev) => ({
                ...prev,
                [name]: isClosed
                    ? response?.message || t('eventRegistration.form.pickupLocationClosed')
                    : null
            }));
            if (isClosed) {
                setError(name, {
                    type: 'manual',
                    message: response?.message || t('eventRegistration.form.pickupLocationClosed')
                });
            }
            else {
                clearErrors(name);
            }
            return isClosed || false;
        }
        catch (error) {
            const apiError = error;
            const errorMsg = apiError?.validationErrors?.[0] ||
                apiError?.error ||
                t('eventRegistration.form.pickupLocationCheckFailed');
            setPickupLocationError((prev) => ({ ...prev, [name]: errorMsg }));
            setError(name, {
                type: 'manual',
                message: errorMsg || 'Unable to verify pickup location right now'
            });
            return true;
        }
        finally {
            setPickupLocationLoading((prev) => ({ ...prev, [name]: false }));
        }
    };
    // Real-time only — no mount re-run
    useEffect(() => {
        if (!open || pickupLocationFieldIds.length === 0)
            return;
        const subscription = watch((value, { name }) => {
            if (!name || !pickupLocationFieldIds.includes(name))
                return;
            void checkPickupLocationById(name, value?.[name]);
        });
        return () => subscription.unsubscribe();
    }, [watch, pickupLocationFieldIds, open]);
    const isPickupLocationLoading = useMemo(() => Object.values(pickupLocationLoading).some(Boolean), [pickupLocationLoading]);
    const validatePickupLocations = async () => {
        const pickupFields = getAllFormFields().filter((f) => String(f.fieldType) === String(FieldType.PICKUPLOCATION) && isBranchingFieldVisible(f));
        if (pickupFields.length === 0)
            return false;
        // Validate only fields that actually have a selected value
        const selectedPickupFields = pickupFields.filter((field) => {
            const selectedValue = getValues(field.id);
            return (selectedValue !== undefined && selectedValue !== null && String(selectedValue).trim() !== '');
        });
        if (selectedPickupFields.length === 0)
            return false;
        // Optional: de-duplicate by selected value to avoid duplicate API calls
        const seenValues = new Set();
        const uniqueSelectedFields = selectedPickupFields.filter((field) => {
            const value = String(getValues(field.id));
            if (seenValues.has(value))
                return false;
            seenValues.add(value);
            return true;
        });
        const results = await Promise.all(uniqueSelectedFields.map((field) => checkPickupLocationById(field.id, String(getValues(field.id)))));
        return results.some(Boolean);
    };
    const onSubmit = async (data) => {
        if (!userId || !stepsFieldsResponses) {
            toast.error(t('errors.missingUserData') || 'Missing user data. Please try again.');
            return;
        }
        const isPickupBlocking = await validatePickupLocations();
        if (isPickupBlocking)
            return;
        setIsSaving(true);
        try {
            // A3: Prevent changing parent questions that can route to undertaking fields.
            const lockedParentFields = getAllFormFields().filter((f) => isUndertakingLinkedParentField(f));
            const changedLockedField = lockedParentFields.find((f) => {
                const initialIds = getInitialSelectedOptionIds(f);
                const currentIds = getSelectedOptionIdsByValue(f, data[f.id]);
                return JSON.stringify(initialIds) !== JSON.stringify(currentIds);
            });
            if (changedLockedField) {
                toast.error(t('events.eventDetails.responsesTab.undertakingOptionCannotBeChanged') ||
                    'This option cannot be changed because it controls an undertaking field.');
                return;
            }
            // Transform form data to the format expected by the API
            const visibleFields = Object.keys(data)
                .filter((fieldId) => !fieldId.endsWith('_reason')) // Filter out reason fields as they're handled with their parent undertaking fields
                .filter((fieldId) => {
                const fieldDefinition = getAllFormFields().find((f) => f.id === fieldId);
                if (!fieldDefinition)
                    return false;
                return isBranchingFieldVisible(fieldDefinition);
            })
                .flatMap((fieldId) => {
                const value = data[fieldId];
                // Find the field definition to check if it has options
                let fieldDefinition = null;
                stepsFieldsResponses.forEach((step) => {
                    const field = step.formFields?.find((f) => f.id === fieldId);
                    if (field)
                        fieldDefinition = field;
                });
                // Additional Attendees: each person's answer to each real sub-question field is its own
                // ordinary entry, tagged with which repeated person (instanceIndex) it belongs to - not a
                // single JSON blob on the parent field. A card the admin removed is simply omitted here,
                // which the backend interprets as "delete this person."
                if (fieldDefinition?.fieldType === FieldType.ADDITIONAL_ATTENDEES) {
                    const people = value?.people || [];
                    const subFieldDefs = referredByFieldsByParent[fieldId] || [];
                    const entries = [];
                    people.forEach((person, personIndex) => {
                        if (!person)
                            return;
                        subFieldDefs.forEach((subField) => {
                            const subValue = person[subField.id];
                            if (subValue === undefined || subValue === '')
                                return;
                            entries.push({
                                fieldId: subField.id,
                                value: subValue,
                                instanceIndex: personIndex
                            });
                        });
                    });
                    return entries;
                }
                const fieldData = {
                    fieldId,
                    value: Array.isArray(value)
                        ? JSON.stringify(value)
                        : value === null || value === undefined || value === ''
                            ? null
                            : String(value)
                };
                // Handle undertaking fields (fieldType 19) specially
                if (fieldDefinition?.fieldType === FieldType.UNDERTAKING) {
                    // For undertaking fields, we need to include the acknowledgment reason
                    const reasonFieldName = `${fieldId}_reason`;
                    const acknowledgmentReason = data[reasonFieldName] || '';
                    fieldData.acknowledgmentReason = acknowledgmentReason;
                    fieldData.value = value ? 'true' : 'false';
                }
                // Handle company dropdown fields (fieldType 18) specially
                else if (fieldDefinition?.fieldType === FieldType.COMPANYDROPDOWN) {
                    if (value && value !== '' && value !== null) {
                        const stringValue = String(value);
                        // Find the selected option from the fresh options (event companies)
                        // We need to get the fresh options that were created in getFieldProps
                        let selectedOption = null;
                        // Get event companies from currentEvent
                        if (currentEvent?.eventCompanies && currentEvent.eventCompanies.length > 0) {
                            const eventCompanyOptions = currentEvent.eventCompanies.map((company, index) => ({
                                id: company.value,
                                label: company.label,
                                value: String(index + 1),
                                isNew: false
                            }));
                            selectedOption = eventCompanyOptions.find((opt) => String(opt.value) === stringValue);
                        }
                        if (selectedOption) {
                            // This is a predefined event company - send the option data with companyId
                            fieldData.options = [
                                {
                                    optionId: selectedOption.id,
                                    optionName: selectedOption.label,
                                    value: selectedOption.value,
                                    companyId: selectedOption.id // Include companyId for proper matching
                                }
                            ];
                        }
                        else {
                            // This is a newly added company - send as new option
                            // For new companies, we need to generate a sequential value
                            const nextValue = (currentEvent?.eventCompanies?.length || 0) + 1;
                            fieldData.options = [
                                {
                                    optionId: null,
                                    optionName: stringValue,
                                    value: nextValue,
                                    companyId: null // No companyId for new options
                                }
                            ];
                        }
                    }
                    else {
                        // No value selected - send null
                        fieldData.value = null;
                    }
                }
                // Handle pickup location dropdown fields (fieldType 20) specially
                else if (fieldDefinition?.fieldType === FieldType.PICKUPLOCATION) {
                    if (value && value !== '' && value !== null) {
                        const stringValue = String(value);
                        const selectedOption = pickupLocationOptions.find((opt) => String(opt.value) === stringValue);
                        if (selectedOption) {
                            fieldData.options = [
                                {
                                    optionId: selectedOption.id,
                                    optionName: selectedOption.label,
                                    value: stringValue
                                }
                            ];
                        }
                        else {
                            fieldData.value = null;
                            fieldData.options = [];
                        }
                    }
                    else {
                        fieldData.value = null;
                        fieldData.options = [];
                    }
                }
                // Handle other fields with options (multi-select, etc.)
                else if (fieldDefinition?.fieldOptions && Array.isArray(value)) {
                    const selectedOptions = fieldDefinition.fieldOptions
                        .filter((opt) => value.includes(String(opt.value)))
                        .map((opt) => ({
                        optionId: opt.id,
                        optionName: opt.label,
                        value: opt.value
                    }));
                    if (selectedOptions.length > 0) {
                        fieldData.options = selectedOptions;
                    }
                }
                return [fieldData];
            });
            // Collect referredBy fields for company dropdowns with external company selected AND event is external
            stepsFieldsResponses.forEach((step) => {
                step.formFields
                    ?.filter((f) => f.fieldType === FieldType.COMPANYDROPDOWN)
                    .forEach((f) => {
                    const fieldId = f.id;
                    const value = data[fieldId];
                    const isExternalCompany = value && value !== '' && value !== null && isNaN(Number(value));
                    const isExternalEvent = currentEvent?.isExternalAllowed || false;
                    // Only collect referredBy fields when event is external AND external company is selected
                    if (isExternalEvent && isExternalCompany && referredByFieldsByParent[fieldId]) {
                        const visibleReferredByFields = referredByFieldsByParent[fieldId].filter((refField) => refField.isEnabled === true || refField.isEnabled === undefined);
                        visibleReferredByFields.forEach((refField) => {
                            const refFieldValue = data[refField.id];
                            // Include the field if it has a value (matching the logic for regular fields)
                            if (refFieldValue !== undefined && refFieldValue !== '') {
                                const refFieldData = {
                                    fieldId: refField.id,
                                    value: Array.isArray(refFieldValue)
                                        ? JSON.stringify(refFieldValue)
                                        : refFieldValue === null ||
                                            refFieldValue === undefined ||
                                            refFieldValue === ''
                                            ? null
                                            : String(refFieldValue)
                                };
                                visibleFields.push(refFieldData);
                            }
                        });
                    }
                });
            });
            // A2: Explicitly clear hidden branching fields so old answers are removed.
            const hiddenBranchingClears = getAllFormFields()
                .filter((f) => !isBranchingFieldVisible(f))
                .map((f) => ({
                fieldId: f.id,
                value: null,
                options: []
            }));
            const hiddenIds = new Set(hiddenBranchingClears.map((f) => f.fieldId));
            const fields = [
                ...visibleFields.filter((f) => !hiddenIds.has(f.fieldId)),
                ...hiddenBranchingClears
            ];
            const request = {
                registrationId: userId,
                fields
            };
            // Call the update API
            await dispatch(updateResponsesAsync(request)).unwrap();
            // Show success toast
            toast.success(t('events.eventDetails.responsesTab.responseUpdatedSuccessfully') ||
                'Response updated successfully!');
            // Refresh grid data to show latest data
            if (eventId && paginationModel) {
                const refreshRequest = {
                    eventId,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize
                };
                dispatch(getAllActiveResponsesByEventIdGroupedByRegistrationAsync(refreshRequest));
            }
            // Close dialog on success
            onClose();
        }
        catch (error) {
            // Show error toast
            const errorMessage = error?.message ||
                error?.error ||
                t('errors.failedToUpdateResponse') ||
                'Failed to update response. Please try again.';
            toast.error(errorMessage);
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleSave = () => {
        handleSubmit(onSubmit)();
    };
    // Create timeline options once to reuse in both desktop and mobile views
    const timelineOptions = useMemo(() => (stepsFieldsResponses || []).map((step) => ({
        step: step.name,
        content: (_jsx(Box, { sx: { mt: 2, mb: 3 }, children: [...(step.formFields || [])]
                .filter((field) => !field.relatedFormFieldId && !field.RelatedFormFieldId) // Hide referredBy fields from main list
                .filter((field) => isBranchingFieldVisible(field))
                .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                .map((field, index) => {
                const isCompanyDropdown = field.fieldType === '18';
                const companyValue = isCompanyDropdown ? watchedValues[field.id] : null;
                // External company: value is not a number (original companies use 1, 2, 3...)
                const isExternalCompany = isCompanyDropdown && companyValue && isNaN(Number(companyValue));
                const isExternalEvent = currentEvent?.isExternalAllowed || false;
                // Show referredBy fields only when event is external AND external company is selected
                const shouldShowReferredBy = isExternalEvent && isExternalCompany;
                const referredByFields = shouldShowReferredBy && referredByFieldsByParent[field.id]
                    ? referredByFieldsByParent[field.id]
                        .filter((f) => f.isEnabled === true || f.isEnabled === undefined) // Only show enabled fields
                        .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                    : [];
                return (_jsxs(Box, { sx: { mb: index < step.formFields.length - 1 ? '12px' : 0 }, children: [renderFieldWithResponse(field), shouldShowReferredBy && referredByFields.length > 0 && (_jsxs(Box, { sx: { width: '100%', mt: 1 }, children: [_jsx(Typography, { variant: 'body2', sx: {
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        mb: 1.5
                                    }, children: t('events.referredBy') }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0 }, children: referredByFields.map((refField, refIndex) => {
                                        const RefFieldComponent = fieldRenderers[refField.fieldType];
                                        // The email field is always the second field (index 1) in the sorted referredBy fields
                                        const isEmailReferredByField = refField.fieldType === '2';
                                        const refFieldProps = getFieldProps(refField); // Get props for referredBy field
                                        const refFieldError = errors[refField.id]?.message;
                                        return RefFieldComponent ? (_jsx(Box, { sx: { mt: 1 }, children: _jsx(RefFieldComponent, { error: refFieldError, ...refFieldProps }) }, refField.id)) : null;
                                    }) })] }))] }, field.id));
            }) }))
    })), [errors, stepsFieldsResponses, watchedValues, referredByFieldsByParent, currentEvent] // Re-compute when form values change
    );
    const dialogContent = (_jsx(Box, { sx: styles.dialogContentStyle, children: _jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsx(Box, { sx: { mt: 2, px: 2 }, children: status === APIStatus.PENDING ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }, children: _jsx(Typography, { children: "Loading form data..." }) })) : stepsFieldsResponses && stepsFieldsResponses.length > 0 ? (_jsx(HTimeline, { options: timelineOptions })) : (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }, children: _jsx(Typography, { children: "No form data available" }) })) }) }) }));
    const actionButtons = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: onClose, disabled: isSaving, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', endIcon: _jsx(CheckOutlined, {}), onClick: handleSave, disabled: isSaving || hasBlockingPickupLocation || isPickupLocationLoading, children: t('actions.saveChanges') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { anchor: 'bottom', title: t('events.eventDetails.responsesTab.editUserResponse'), open: open, onClose: onClose, showCloseButton: true, dialogActions: actionButtons, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: _jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsx(Box, { sx: { px: 2 }, children: status === APIStatus.PENDING ? (_jsx(Box, { sx: {
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: 200
                            }, children: _jsx(Typography, { children: "Loading form data..." }) })) : stepsFieldsResponses && stepsFieldsResponses.length > 0 ? (_jsx(HTimeline, { options: timelineOptions })) : (_jsx(Box, { sx: {
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: 200
                            }, children: _jsx(Typography, { children: "No form data available" }) })) }) }) }) }));
    }
    return (_jsx(CustomDialog, { backgroundVariant: 'white', titleWithIcon: t('events.eventDetails.responsesTab.editUserResponse'), open: open, onClose: onClose, maxWidth: 'md', titleIcon: '/assets/images/dialog-pencil.png', actions: actionButtons, children: dialogContent }));
};
export default EditUserResponseDialog;
